import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, Wrench, Clock, Inbox, RefreshCw, Trash2, Check } from "lucide-react";
import { BUSINESS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", bg: "#e1effe", color: "#1a56db" },
  contacted: { label: "Contacted", bg: "#fef3c7", color: "#b45309" },
  done: { label: "Done", bg: "#dcfce7", color: "#15803d" },
};

const NEXT_STATUS = { new: "contacted", contacted: "done", done: "new" };

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function Staff() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data);
    } catch (e) {
      toast.error("Could not load requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cycleStatus = async (lead) => {
    const next = NEXT_STATUS[lead.status] || "new";
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: next } : l)));
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status: next });
    } catch {
      toast.error("Update failed.");
      load();
    }
  };

  const remove = async (lead) => {
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
    try {
      await axios.delete(`${API}/leads/${lead.id}`);
      toast.success("Request removed.");
    } catch {
      toast.error("Delete failed.");
      load();
    }
  };

  const visible = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    done: leads.filter((l) => l.status === "done").length,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f8fafc" }} data-testid="staff-page">
      <header className="border-b border-white/10" style={{ backgroundColor: "var(--navy)" }}>
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: "var(--brand-blue)" }}>
              <Inbox size={18} className="text-white" />
            </span>
            <div>
              <h1 className="text-sm font-semibold text-white">Staff Dispatch Inbox</h1>
              <p className="text-[11px] text-slate-300">{BUSINESS.name}</p>
            </div>
          </div>
          <Link to="/" data-testid="back-to-site" className="flex items-center gap-1.5 text-sm font-semibold text-slate-200 hover:text-white">
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {["all", "new", "contacted", "done"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className="rounded-full border px-4 py-2 text-sm font-semibold capitalize transition-colors"
                style={filter === f
                  ? { backgroundColor: "var(--brand-blue)", borderColor: "var(--brand-blue)", color: "#fff" }
                  : { backgroundColor: "#fff", borderColor: "#e2e8f0", color: "#334155" }}
              >
                {f} ({counts[f]})
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="refresh-leads" className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {loading && leads.length === 0 ? (
          <p className="py-20 text-center text-slate-400">Loading requests…</p>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white py-20 text-center" data-testid="empty-state">
            <Inbox size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 font-semibold text-slate-600">No service requests yet</p>
            <p className="mt-1 text-sm text-slate-400">New requests from the website will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((lead) => {
              const meta = STATUS_META[lead.status] || STATUS_META.new;
              return (
                <div key={lead.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`lead-card-${lead.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-slate-900">{lead.name}</h3>
                      <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ backgroundColor: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Clock size={13} /> {timeAgo(lead.created_at)}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[15px]">
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-2 font-semibold text-slate-900 hover:text-blue-700">
                      <Phone size={15} style={{ color: "var(--brand-blue)" }} /> {lead.phone}
                    </a>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-700">
                        <Mail size={15} style={{ color: "var(--brand-blue)" }} /> {lead.email}
                      </a>
                    )}
                    {lead.city && (
                      <span className="flex items-center gap-2 text-slate-600">
                        <MapPin size={15} style={{ color: "var(--brand-blue)" }} /> {lead.city}
                      </span>
                    )}
                  </div>

                  {lead.service && (
                    <p className="mt-3 flex items-center gap-2 text-sm">
                      <Wrench size={15} style={{ color: "var(--brand-blue)" }} />
                      <span className="rounded-md px-2 py-1 font-semibold" style={{ backgroundColor: "var(--brand-blue-light)", color: "var(--brand-blue-dark)" }}>
                        {lead.service}
                      </span>
                    </p>
                  )}

                  {lead.message && (
                    <p className="mt-3 rounded-lg bg-slate-50 p-3 text-[15px] leading-relaxed text-slate-700">{lead.message}</p>
                  )}

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => cycleStatus(lead)}
                      data-testid={`cycle-status-${lead.id}`}
                      className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: "var(--brand-blue)" }}
                    >
                      <Check size={15} /> Mark {NEXT_STATUS[lead.status]}
                    </button>
                    <button
                      onClick={() => remove(lead)}
                      data-testid={`delete-lead-${lead.id}`}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
